import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { PhoneNumberInput } from "@/components/ui/forms/phoneNumberInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { TextAreaInput } from "@/components/ui/forms/textareaInput"
import { Label } from "@/components/ui/baseUi/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import { type SimpleFormik } from "@/typings/formik"
import { InformationIcon } from "@images/svgIcons/informationAlertIcon"
import { type AddressAutofillRetrieveResponse } from "@mapbox/search-js-core"
import { AddressAutofill } from "@mapbox/search-js-react"
import { type BoxFormikType, type BoxInputKey } from "@modules/box/boxFormikType"
import { Config } from "@modules/config"
import { word } from "@modules/core/utils/i18n"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { useCountrySubdivisionsListArray } from "@modules/language/utils/countries"
import type { TCountryCode } from "countries-list"
import { CheckIcon, ChevronsUpDownIcon, CircleQuestionMark } from "lucide-react"
import { useEffect, useState } from "react"

type BoxMainInfoFormProps = {
  getFormErrorMessage: (name: BoxInputKey) => React.JSX.Element | undefined
  formik: SimpleFormik<BoxFormikType, BoxInputKey>
} & React.ComponentProps<"div">

export function BoxMainInfoForm({ formik, getFormErrorMessage, className, ...props }: BoxMainInfoFormProps) {
  const countryOptions = useCountrySubdivisionsListArray()
  const timezones = Intl.supportedValuesOf("timeZone")
  const canUseTimeZone = useFeatureToggle(FeatureKeys.TimeZone)
  const [openCountrySelect, setOpenCountrySelect] = useState(false)
  const [openTimezoneSelect, setOpenTimezoneSelect] = useState(false)

  useEffect(() => {
    if (formik.values.country && !formik.values.phoneNumberCountryCode)
      formik.setFieldValue("phoneNumberCountryCode", formik.values.country)
  }, [formik.values.country])

  return (
    <div
      className={cn("mb-0 flex w-full flex-col gap-5 rounded-2xl bg-neutral-50 p-8 xl:w-8/12", className)}
      {...props}
    >
      <div className="flex flex-col">
        <Text fontStyle="HS">{word("box.form.header")}</Text>
        <Text fontStyle="BM" className="text-neutral-800">
          {word("box.form.subtitle")}
        </Text>
      </div>

      {/* Name input */}
      <StandardInput
        label={word("box.form.name.label")}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        name="name"
        error={getFormErrorMessage("name")}
        isRequiredInput
        containerClassName="flex-row items-center gap-0"
      >
        <Tooltip>
          <TooltipTrigger render={<CircleQuestionMark className="ml-4 text-textDefault-caption" strokeWidth={1} />} />

          <TooltipContent side="bottom" className="max-w-100 rounded-xl p-0">
            <div className="p-0 text-start">
              <div className="flex flex-row items-center gap-2 border-b border-neutral-200 px-4 py-3">
                <InformationIcon backgroundColor="transparent" color="currentColor" />
                <Text fontStyle="BL">{word("box.form.name.changeWarning.title")}</Text>
              </div>

              <div className="flex flex-col gap-2 px-4 py-3">
                <div className="flex flex-col">
                  <Text fontStyle="BM">{word("box.form.name.changeWarning.visibility.header")}</Text>
                  <Text fontStyle="BS">{word("box.form.name.changeWarning.visibility.content")}</Text>
                </div>
                <div className="flex flex-col">
                  <Text fontStyle="BM">{word("box.form.name.changeWarning.confusion.header")}</Text>
                  <Text fontStyle="BS">{word("box.form.name.changeWarning.confusion.content")}</Text>
                </div>
                <div className="flex flex-col">
                  <Text fontStyle="BM">{word("box.form.name.changeWarning.administrative.header")}</Text>
                  <Text fontStyle="BS">{word("box.form.name.changeWarning.administrative.content")}</Text>
                </div>
                <div className="flex flex-col">
                  <Text fontStyle="BM">{word("box.form.name.changeWarning.partners.header")}</Text>
                  <Text fontStyle="BS">{word("box.form.name.changeWarning.partners.content")}</Text>
                </div>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </StandardInput>

      {/* Description input */}
      <TextAreaInput
        isRequiredInput
        label={word("box.form.description.label")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        name="description"
        error={getFormErrorMessage("description")}
        textareaProps={{
          onChange: (e) => {
            formik.setFieldValue("description", e.target.value)
          },
          className: "field-sizing-fixed",
          placeholder: word("box.form.description.label"),
          rows: 12,
        }}
      />

      {/* Address input */}
      <AddressAutofill
        accessToken={Config.MAPBOX_API_KEY}
        options={{ streets: false }}
        onRetrieve={(res: AddressAutofillRetrieveResponse) => {
          const metadata = res.features[0]!.properties.metadata as
            | { iso_3166_1?: string; iso_3166_2?: string }
            | undefined
          let countryCode: string | undefined
          if (metadata?.iso_3166_1) {
            countryCode = metadata.iso_3166_1.toUpperCase()
          } else if (metadata?.iso_3166_2) {
            const parts = metadata.iso_3166_2.split("-")
            countryCode = parts[0]?.toUpperCase()
          } else {
            countryCode = res.features[0]!.properties.country_code?.toUpperCase()
          }
          const foundCountry = countryCode ? countryOptions.find((item) => item.code === countryCode) : undefined
          if (foundCountry) formik.setFieldValue("country", foundCountry.code)

          formik.setFieldValue("location", {
            longitude: res.features[0]!.geometry.coordinates[0],
            latitude: res.features[0]!.geometry.coordinates[1],
          })
          formik.setFieldValue("postalCode", res.features[0]!.properties.postcode)
          formik.setFieldValue("additionalAddress", res.features[0]!.properties.address_line2)
          formik.setFieldValue("city", res.features[0]!.properties.address_level2)
        }}
        theme={{
          variables: {
            unitHeader: "28px",
            fontFamily: '"Trenda", sans-serif',
            border: "2px solid #424242",
            borderRadius: "0",
            boxShadow: "0 0 1px 1px rgba(0,0,0,0.1)",
            colorText: "var(--color-white)",
            colorPrimary: "var(--primary-500)",
            colorSecondary: "var(--secondary-500)",
            colorBackgroundHover: "var(--neutral-300)",
            colorBackground: "var(--neutral-100)",
          },
          cssText: `
            .Results {
                z-index: 100000;
                border-radius: 20px;
            }
            .ResultsAttribution {
                  display: none;
                }
        `,
        }}
        popoverOptions={{ offset: 0 }}
      >
        <div className="mt-4 mb-5 flex flex-col gap-3">
          <Text fontStyle="TM">{word("box.form.address.title")}</Text>
          <Text fontStyle="BM" className="mb-3 text-neutral-700">
            {word("box.form.address.subtitle")}
          </Text>

          <div className="flex flex-col gap-4 border-l border-neutral-200 py-1 pl-4">
            <StandardInput
              name="address"
              isRequiredInput
              label={word("box.form.address.address.label")}
              getFieldProps={formik.getFieldProps}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage("address")}
              containerClassName="relative"
              inputProps={{
                placeholder: word("box.form.address.address.label"),
                onBlur: (e) => {
                  if (formik.handleBlur) formik.handleBlur(e, "address")
                },
              }}
            />

            <StandardInput
              name="additionalAddress"
              getFieldProps={formik.getFieldProps}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage("additionalAddress")}
              inputProps={{
                placeholder: word("box.form.address.additional_address.placeholder"),
              }}
            />

            <StandardInput
              name="postalCode"
              isRequiredInput
              label={word("box.form.address.postalCode.label")}
              getFieldProps={formik.getFieldProps}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage("postalCode")}
              inputProps={{
                placeholder: word("box.form.address.postalCode.label"),
                onBlur: (e) => {
                  if (formik.handleBlur) formik.handleBlur(e, "postalCode")
                },
                onChange: (e) => {
                  if (formik.handleChange) formik.handleChange(e, "postalCode")
                },
              }}
            />

            <StandardInput
              name="city"
              isRequiredInput
              label={word("box.form.address.city.label")}
              getFieldProps={formik.getFieldProps}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage("city")}
              inputProps={{
                placeholder: word("box.form.address.city.label"),
                onBlur: (e) => {
                  if (formik.handleBlur) formik.handleBlur(e, "city")
                },
                onChange: (e) => {
                  if (formik.handleChange) formik.handleChange(e, "city")
                },
              }}
            />

            {/* Country input */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="country" className="uppercase">
                {word("box.form.address.country.label")}
              </Label>
              <Popover open={openCountrySelect} onOpenChange={setOpenCountrySelect}>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openCountrySelect}
                      className="h-11 w-full justify-between bg-transparent"
                    >
                      {formik.values.country
                        ? countryOptions.find((countryOption) => countryOption.code === formik.values.country)?.label
                        : word("box.form.address.country.label")}

                      <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
                <PopoverContent className="w-65 border-0 p-0" align="start">
                  <Command
                    className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
                    filter={(value, search) => {
                      if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase())))
                        return 1
                      return 0
                    }}
                  >
                    <CommandInput placeholder={word("global.searchCountry")} className="text-textDefault-body" />
                    <CommandList>
                      <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                      <CommandGroup>
                        {countryOptions.map((countryOption) => (
                          <CommandItem
                            key={countryOption.code}
                            value={countryOption.label}
                            onSelect={(currentValue) => {
                              const foundCountry = countryOptions.find((item) => item.label === currentValue)
                              formik.setFieldValue("country", foundCountry?.code)
                              setOpenCountrySelect(false)
                            }}
                            className="cursor-pointer hover:bg-neutral-200"
                          >
                            <CheckIcon
                              className={cn(
                                "mr-2 h-4 w-4",
                                formik.values.country === countryOption.code ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {countryOption.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {canUseTimeZone && (
              <Popover open={openTimezoneSelect} onOpenChange={setOpenTimezoneSelect}>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openTimezoneSelect}
                      className="h-11 w-full justify-between bg-transparent"
                    >
                      {formik.values.zoneId
                        ? timezones.find((timezone) => timezone === formik.values.zoneId)
                        : word("box.form.address.timezone.label")}

                      <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
                <PopoverContent className="w-65 border-0 p-0" align="start">
                  <Command className="**:data-[slot=command-input-wrapper]:bg-neutral-50">
                    <CommandInput
                      placeholder={word("box.form.address.timezone.placeholder")}
                      className="text-textDefault-body"
                    />
                    <CommandList>
                      <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                      <CommandGroup>
                        {timezones.map((timezone, index) => (
                          <CommandItem
                            key={index}
                            value={timezone}
                            onSelect={(currentValue) => {
                              const foundTimezone = timezones.find((timezone) => timezone === currentValue)
                              formik.setFieldValue("zoneId", foundTimezone)
                              setOpenTimezoneSelect(false)
                            }}
                            className="cursor-pointer hover:bg-neutral-200"
                          >
                            <CheckIcon
                              className={cn(
                                "mr-2 h-4 w-4",
                                formik.values.zoneId === timezone ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {timezone}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </AddressAutofill>

      {/* Contact input */}
      <div className="mt-4 mb-5 flex flex-col">
        <Text fontStyle="TM">{word("box.form.contact.title")}</Text>
        <Text fontStyle="BM" className="mb-3 text-neutral-700">
          {word("box.form.contact.subtitle")}
        </Text>

        <div className="flex flex-col gap-4 border-l border-neutral-200 py-1 pl-4">
          <PhoneNumberInput
            isRequiredInput
            label={word("global.form.label.phoneNumber")}
            getFieldProps={formik.getFieldProps}
            name="phoneNumber"
            countryCodeName="phoneNumberCountryCode"
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("phoneNumber")}
            countryCode={formik.values.phoneNumberCountryCode as TCountryCode}
          />

          <StandardInput
            name="email"
            isRequiredInput
            label={word("box.form.email.label")}
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("email")}
            inputProps={{
              onBlur: (e) => {
                if (formik.handleBlur) formik.handleBlur(e, "email")
              },
              onChange: (e) => {
                if (formik.handleChange) formik.handleChange(e, "email")
              },
            }}
          />
        </div>
      </div>

      {/* Company input */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col">
          <Text fontStyle="TM">{word("box.form.company.title")}</Text>
          <Text fontStyle="BM" className="text-neutral-700">
            {word("box.creation.page.legal.description")}
            <br />
            {word("box.creation.page.legal.alert")}
          </Text>
        </div>

        <div className="border-l border-neutral-200 py-1 pl-4">
          <StandardInput
            isRequiredInput
            label={word("box.creation.page.legal.label")}
            inputProps={{
              placeholder: word("box.creation.page.legal.placeholder"),
            }}
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            name="siren"
            error={getFormErrorMessage("siren")}
          />
        </div>
      </div>
    </div>
  )
}
